export interface Adresse {
  gebietsnr?: number;
  hausnr?: string;
  ikz?: string;
  ort?: string;
  plz?: string;
  strasse?: string;
  zusaetze?: string;
}

export interface Berufsinfo {
  beruf?: Beruf;
  berufsbezeichnung?: string;
  beschaeftigtBei?: string;
  beschaeftigtSeit?: string;
  beschaeftigungsart?: Beschaeftigungsart;
  selbstaendig?: boolean;
}

export interface Beruf {
  hostKey?: number;
  name?: string;
  isAlwaysSelfEmployed?: boolean;
  isNeverSelfEmployed?: boolean;
}

export interface Beschaeftigungsart {
  hostKey?: number;
  name?: string;
}

export interface Identifikationsnachweis {
  ausstellendeBehoerde?: string;
  ausstellungsdatum?: string;
  ablaufdatum?: string;
  ausweisNr?: string;
  ausweisart?: string;
  firmenbuchNr?: string;
  uid?: string;
}

export interface Fatca {
  steuersitz?: string;
  steuernummer?: string;
  giltAb?: number;
  giltBis?: number;
}

export interface Kundenkommunikation {
  art: string;
  typ: string;
  laendervorwahl: string;
  vorwahl: string;
  telefonnummer: string;
  display?: string;
  unwetterwarnung?: boolean;
  fondswechsel?: boolean;
}

export interface Email {
  email?: string;
  confirmed?: boolean;
  changed?: boolean;
  teilKommunikation?: boolean;
  elektronischeKommunikation?: boolean;
}

export interface Krankenkasse {
  hostKey?: number;
  name?: string;
}

export interface Sozialversicherungsnummer {
  geburtsdatum: string;
  versicherungsnummer: number;
}

export interface Tippgeber {
  tippgeberAbber?: string;
  tippgeberSeit?: string;
}

export interface Firmenkundenmanagement {
  fkmKundeSeit?: string;
  fkmStatus?: string;
  konzernnummer?: string;
  firmennummer?: string;
  kennungAWKnoten?: string;
  zielgruppeAWKnoten?: string;
  brancheAWKnoten?: string;
  jahresBestandsPraemie: number;
  anzahlMitarbeiter: number;
  zugangsweg?: string;
  rolleImKonzern?: string;
  standort?: string[];
}

export interface KbiDaten {
  kbiNummer: number;
  kbiPositionsnummer: number;
  bestandsart: string;
  zugrundeliegendeVollmacht: string;
  zuweisungsgrund: string;
  giltAb: string;
}

export interface Bankdaten {
  blz?: number;
  swiftBic?: string;
  abi?: string;
  cab?: string;
  giin?: string;
}

export interface FkmBetreuung {
  abbernummer?: number;
  abbername?: string;
  orgDesVermittlers?: string;
  exklusivrecht?: boolean;
  standort?: string[];
}

export interface ConnectedPartnerStammdaten {
  loggedInUser?: boolean;
  partnerNr: number;
  name: string;
  vorname: string;
  titel: string;
  postTitel: string;
  geschlecht: string;
  unbekannterGeburtstag: boolean;
  gebdatum: string;
  sozversanstalt: Krankenkasse;
  adresse: Adresse;
  bearbeitetAm?: string;
}

export interface PartnerMitVertragUndAntrag {
  partner?: Partner;
  kundenVertraege?: KundenVertrag[];
  kundenAntraege?: KundenAntrag[];
  additionalPartnerObjekteForJuristischePerson?: Partner[];
}

export interface KundenVertrag {
  rollen?: string[];
  sparte?: string;
  vertragsnr?: number;
  mlvVertrag?: boolean;
  jabinLink?: string;
}

export interface KundenAntrag {
  sparte?: string;
  antragNr?: number;
}

export interface PartnerSearchCriteria {
  search: string;
}

export interface DataDescription {
  description?: string;
  key?: string;
}

export enum PartnerFamilienstand {
  LEDIG = 'LEDIG',
  LEBENSGEMEINSCHAFT = 'LEBENSGEMEINSCHAFT',
  VERHEIRATET = 'VERHEIRATET',
  VERWITWET = 'VERWITWET',
  GESCHIEDEN = 'GESCHIEDEN',
  UNBEKANNT = 'UNBEKANNT'
}

export enum PartnerGeschlecht {
  W = 'W',
  M = 'M',
  D = 'D'
}

export enum IdentifikationsnachweisAusweisart {
  UNBEKANNT = 'UNBEKANNT',
  FUEHRERSCHEIN = 'FUEHRERSCHEIN',
  ECARD = 'ECARD',
  GEBURTSURKUNDE = 'GEBURTSURKUNDE',
  OEFFENTLICHER_DIENSTAUSWEIS = 'OEFFENTLICHER_DIENSTAUSWEIS',
  PERSONALAUSWEIS = 'PERSONALAUSWEIS',
  REISEPASS = 'REISEPASS',
  REISEPASS_ELTERN = 'REISEPASS_ELTERN',
  SCHUELERAUSWEIS = 'SCHUELERAUSWEIS'
}

export interface Partner {
  noQAddressCheck?: boolean;
  adresse?: Adresse;
  beruf?: Berufsinfo;
  berufstitel?: string;
  datennutzungZugestimmt?: boolean;
  datennutzungGesundheitsdatenZugestimmt?: boolean;
  familienstand?: string;
  gebdatum?: string;
  verstorben?: string;
  geburtsland?: string;
  geburtsort?: string;
  geschlecht?: string;
  anrede?: string;
  id?: string;
  identifikationsnachweis?: Identifikationsnachweis;
  juristischePerson?: boolean;
  fatca?: Fatca[];
  eigentuemer?: number[];
  vertretungsbefuegtePersonen?: number[];
  kollektivart?: string;
  kundenkommunikation?: Kundenkommunikation[];
  email?: Email;
  name?: string;
  nationalitaet?: string;
  partnerNr?: number;
  uidSteuerNummer?: string;
  postTitel?: string;
  rechtsform?: string;
  sozversanstalt?: Krankenkasse;
  sozversnr?: Sozialversicherungsnummer;
  titel?: string;
  timeStamp?: string;
  unbekannterGeburtstag?: boolean;
  vorname?: string;
  auslsozversnr?: string;
  customerPortalUser?: boolean;
  merkurEmployee?: boolean;
  adressforschung?: boolean;
  tippgeber?: Tippgeber;
  bearbeitetAm?: string;
  firmenbuchLand?: string;
  firmensitzLand?: string;
  selfDisclosureAvailable?: boolean;
  passiveNonFinancialEntity?: boolean;
  einzelunternehmenName?: string;
  einzelunternehmen?: boolean;
  // Flag ob der Partner ein FKM-Kunde ist
  fkmKunde?: boolean;
  // FKM Daten (wenn fkmKunde == true)
  fkm?: Firmenkundenmanagement;
  kbiDaten?: KbiDaten;
  bankdaten?: Bankdaten;
  zuhandenPerson?: string;
  vorregistrierungZumKp?: boolean;
  fkmBetreuung?: FkmBetreuung[];
}
